import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Container, Buttons, HeaderContainer, BackButton, ValidationAlert } from '../../style';
import { SmallInput, PageHeader, Button } from 'scorer-ui-kit';

import useLocalStorage from '../../hooks/useLocalstorage';
import { MenuContext, PathContext } from '../../App';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const InputContainer = styled.div`
  display: flex;
  width: inherit;
  margin: 5px 0 0 0;
  justify-content: space-between;
`;

const JobsListTitle = styled.div`
  display: flex;
  width: inherit;
  margin: 5px 0 0 0;
  justify-content: space-between;
  padding: 20px 0 0 0;
  border-top: 1px solid grey;
`;

const SelectFieldBox = styled.select`
  width: 104px;
  height: 30px;
  padding: 0 0 0 10px;
  border-radius: 3px;
  outline: none;
  font-family: ${({ theme }) => theme.fontFamily.ui};
  font-size: 14px;
  color: #767676;
  margin-top: 5px;
`;

const Position = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
  margin: 0 0 10px 0;
`;

const SmallInputContainer = styled.div`
  display: flex;
  width: auto;
  margin: 0 30px 0 0; 
`;

const SmallInputBox = styled(SmallInput)`
  width: 104px;
  padding: 0 14px 0 0;
  border-radius: 3px;
  outline: none;
  font-family: ${({ theme }) => theme.fontFamily.ui};
  font-size: 12px;
  color: #767676;
  margin-top: -5px;
`;

const InputHeader = styled.label`
  font-family: ${({ theme }) => theme.fontFamily.ui};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.35px;
  color: #7f7f7f;
  margin-bottom: 7px;
`;

const InputField = styled.div`
  display: flex;
  flex-direction: column;
`;

const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? 'lightblue' : '#dde7ed',
  display: 'flex',
  padding: '5px',
  overflow: 'auto',
});

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  padding: '10px',
  margin: '5px',
  background: isDragging ? '#c0ffe9' : '#f0fffa',
  ...draggableStyle,
});


const PlaceCameras = () => {
  const { t } = useTranslation(['SetupCameras', 'Common']);
  const [validationAlert, setValidationAlert] = useState<{ key?: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });

  const { completedStep, setSelectedStep, setCompletedStep } = useContext(MenuContext);
  const { pathUpdated } = useContext(PathContext);
  const { floorPlanStorage, setFloorPlanStorage } = useLocalStorage();

  const [jobspool, setJobspool] = useState([]);
  const [tasks, setTasks] = useState(floorPlanStorage.tasks.length !== 0 ? floorPlanStorage.tasks : [{'id':0, 'name':'task0', 'jobList':[]}]);
  const [selectedTask, setSelectedTask] = useState(0);

  const [ maxTaskID, setMaxTaskID ] = useState(floorPlanStorage.maxTaskID ? floorPlanStorage.maxTaskID : 0);

  //関連待ちjobpoolを作成
  useEffect(() => {
    //全job取得
    const jobs = JSON.parse(JSON.stringify(floorPlanStorage.jobs));
    for (let i=0; i < jobs.length; i++) {
      delete jobs[i].job_start;
      delete jobs[i].job_end;
      delete jobs[i].manual;
      delete jobs[i].manual_original;
    }
    //全関連済みjob ID取得
    const associatedpool = [];
    if (floorPlanStorage.tasks){
      for (let i=0; i < floorPlanStorage.tasks.length; i++) {
        if (floorPlanStorage.tasks[i]){
          for (let k=0; k < floorPlanStorage.tasks[i].jobList.length; k++) {
            associatedpool.push(floorPlanStorage.tasks[i].jobList[k].id);
          }
        }
      }
    }
    //差分取る
    const newjobspool = [];
    for (let i=0; i < jobs.length; i++) {
      if(jobs[i].type === 'repeatable' || associatedpool.indexOf(jobs[i].id) === -1) 
        newjobspool.push(jobs[i]);
    }
    setJobspool(newjobspool);
  }, [floorPlanStorage.jobs, floorPlanStorage.tasks]);




  /*---------dnd---------*/
  //同テーブル内の順位移動
  const reorderJobsPool = useCallback((startIndex, endIndex) => {
    const newjobspool = [...jobspool];
    const removed = newjobspool.splice(startIndex, 1)[0];
    newjobspool.splice(endIndex, 0, removed);
    setJobspool(newjobspool);
  }, [jobspool]);
  const reorderTasksPool = useCallback((startIndex, endIndex) => {
    const newtasks = [...tasks];
    const removed = newtasks[selectedTask].jobList.splice(startIndex, 1)[0];
    newtasks[selectedTask].jobList.splice(endIndex, 0, removed);
    setTasks(newtasks);
  }, [tasks, selectedTask]);

  //関連付け
  const move = useCallback((source, destination) => {
    const newjobspool = [...jobspool];
    const newtasks = [...tasks];

    if (source.droppableId === 'jobsPool'){//jobsPool → tasksPool, 関連付け
      let removed = [];
      if (newjobspool[source.index].type === 'repeatable'){
        removed = newjobspool.slice(source.index, source.index+1)[0];
      }else{
        removed = newjobspool.splice(source.index, 1)[0];
      }
      newtasks[selectedTask].jobList.splice(destination.index, 0, removed);

    }else{//tasksPool → jobsPool, 関連外し
      const removed = newtasks[selectedTask].jobList.splice(source.index, 1)[0];
      if (removed.type !== 'repeatable'){
        newjobspool.splice(destination.index, 0, removed);
      }
    }

    setJobspool(newjobspool);
    setTasks(newtasks);
  }, [jobspool, tasks, selectedTask]);

  //dnd移動判定
  const onDragEnd = useCallback((result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    //同テーブル内の順位移動
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'jobsPool') {
        reorderJobsPool(source.index, destination.index);
      } else {
        reorderTasksPool(source.index, destination.index);
      }
    
    //関連付け
    } else {
      move(source, destination);
    }

    setCompletedStep(15);    
  }, [reorderJobsPool, reorderTasksPool, move, setCompletedStep]);
  /*---------dnd---------*/

  /*---------tasks---------*/
  const addTask = useCallback(() => {
    const newtask:any = {};
    newtask.id = maxTaskID + 1;
    newtask.name = 'task'+newtask.id;
    newtask.jobList = [];
    
    //idに重複しているかどうかチェック
    const nameList = [];
    for(let i = 0; i < tasks.length; i++) {//既存name取得
      nameList.push(tasks[i].name);
    }
    if (nameList.indexOf(newtask.name) !== -1){
      newtask.name = '_task'+newtask.id;
    }

    setSelectedTask(tasks.length);
    setTasks([...tasks, newtask]);
    setMaxTaskID(maxTaskID + 1);
    setCompletedStep(15);
  }, [maxTaskID, tasks, setCompletedStep]);
  
  //task名変更
  const renameTask = useCallback((ev) => {
    //同名判定
    for(const i in tasks){
      if(tasks[i].name === ev.target.value) {
        setValidationAlert({ key: new Date(), message: '同名のタスクが存在しております。タスク名：' + ev.target.value, type: 'error' });
        return;
      }
    }

    //task名変更したら,tasksetと関連付け済みのtaskも変更
    if (floorPlanStorage.tasksets){
      const newtasksets = JSON.parse(JSON.stringify(floorPlanStorage.tasksets));
      for (let i = newtasksets.length-1; i >= 0; i--) {
        for (let index = newtasksets[i].taskList.length-1; index >= 0; index--){
          if (newtasksets[i].taskList[index].id === tasks[selectedTask].id){
            newtasksets[i].taskList[index].name = ev.target.value;
          }
        }
      }
      setFloorPlanStorage({tasksets: newtasksets});
    }

    const newtasks = [...tasks];
    newtasks[selectedTask].name = ev.target.value;
    setTasks(newtasks);
    setCompletedStep(15);
  }, [tasks, selectedTask, floorPlanStorage.tasksets, setFloorPlanStorage, setCompletedStep]);
  
  //task削除
  const delTask = useCallback(() => {
    const newtasks = [...tasks];
    if (newtasks.length === 1) {
      return false;
    }
    
    //taskを削除したら,tasksetとの関連付けも削除
    if (floorPlanStorage.tasksets){
      const tasksets = floorPlanStorage.tasksets;
      for (let i = tasksets.length-1; i >= 0; i--) {
        for (let index = tasksets[i].taskList.length-1; index >= 0; index--){
          if (tasksets[i].taskList[index].id === tasks[selectedTask].id){
            tasksets[i].taskList.splice(index, 1);
          }
        }
      }
      setFloorPlanStorage({tasksets});
    }
    
    //関連付け済みのjobをpoolに返す
    const removedTask = newtasks.splice(selectedTask, 1)[0];
    const newjobspool = [...jobspool];
    for (let i=0; i < removedTask.jobList.length; i++) {
      if (removedTask.jobList[i].type !== 'repeatable') {
        newjobspool.push(removedTask.jobList[i]);
      }
    }

    setJobspool(newjobspool);
    setTasks(newtasks);
    setFloorPlanStorage({tasks: newtasks});
    setSelectedTask(0);
    setCompletedStep(15);
  }, [tasks, selectedTask, jobspool, floorPlanStorage, setFloorPlanStorage, setCompletedStep]);

  const getSelectedTask = useCallback((ev) => {
    for(const i in tasks){
      if(tasks[i].name === ev.target.value) {
        setSelectedTask(Number(i));
        break;
      }
    }
  }, [tasks]);


  /*---------route---------*/
  const onContinue = useCallback((index: number) => {
    for (let i=0; i < tasks.length; i++) {
      if (tasks[i].jobList.length === 0){
        setValidationAlert({ key: new Date(), message: 'ジョブと関連付けされていない空白のタスクがあります。空白のタスクは削除してください。', type: 'error' });
        return;
      }
    }



    //開始条件が同じのjobがあるかどうかを判定
    for (let i=0; i < tasks.length; i++) {
      for (let k=0; k < tasks[i].jobList.length; k++) {
        for (let n = 0; n < floorPlanStorage.jobs.length; n++) {
          //まずjob start詳細データ取得
          if (tasks[i].jobList[k].id === floorPlanStorage.jobs[n].id){
            const checkJobStart = JSON.parse(JSON.stringify(floorPlanStorage.jobs[n].job_start));

            //そして同タスク内の他のjobと比較
            for (let kk=0; kk < tasks[i].jobList.length; kk++) {
              if ( k === kk ){
                continue;
              }
              for (let nn = 0; nn < floorPlanStorage.jobs.length; nn++) {
                if (tasks[i].jobList[kk].id === floorPlanStorage.jobs[nn].id){

                  // typeの長さor中身が一致しない
                  if ( checkJobStart.type.length !== floorPlanStorage.jobs[nn].job_start.type.length ){
                    break;
                  }
                  if ( checkJobStart.type.length === floorPlanStorage.jobs[nn].job_start.type.length ){
                    const c = checkJobStart.type.filter(t => ! floorPlanStorage.jobs[nn].job_start.type.includes(t));
                    if (c.length !== 0) {
                      break;
                    }
                  }
                  
                  
                  // Start.typeが一致する場合、開始条件が同じになる可能性がある
                  let count = 0;
                  for (let loop = 0; loop < checkJobStart.type.length; loop++) {
                    switch(checkJobStart.type[loop]){
                    case 'PoseCategory':
                      if ( checkJobStart.pose_category           === floorPlanStorage.jobs[nn].job_start.pose_category &&
                           checkJobStart.pose_category_threshold === floorPlanStorage.jobs[nn].job_start.pose_category_threshold &&
                           checkJobStart.pose_category_time      === floorPlanStorage.jobs[nn].job_start.pose_category_time){
                        count++; 
                      }
                      break;
                    case 'RightHandCategory':
                      if ( checkJobStart.right_hand_category           === floorPlanStorage.jobs[nn].job_start.right_hand_category &&
                           checkJobStart.right_hand_category_threshold === floorPlanStorage.jobs[nn].job_start.right_hand_category_threshold &&
                           checkJobStart.right_hand_category_time      === floorPlanStorage.jobs[nn].job_start.right_hand_category_time){
                        count++; 
                      }
                      break;
                    case 'LeftHandCategory':
                      if ( checkJobStart.left_hand_category           === floorPlanStorage.jobs[nn].job_start.left_hand_category &&
                           checkJobStart.left_hand_category_threshold === floorPlanStorage.jobs[nn].job_start.left_hand_category_threshold &&
                           checkJobStart.left_hand_category_time      === floorPlanStorage.jobs[nn].job_start.left_hand_category_time){
                        count++; 
                      }
                      break;
                    case 'BodyParts':
                      if ( checkJobStart.body_parts      === floorPlanStorage.jobs[nn].job_start.body_parts &&
                           checkJobStart.body_area_id    === floorPlanStorage.jobs[nn].job_start.body_area_id &&
                           checkJobStart.body_parts_time === floorPlanStorage.jobs[nn].job_start.body_parts_time){
                        count++; 
                      }
                      break;
                    }
                  }
                  if ( count === checkJobStart.type.length ){
                    let msg = 'タスク['+tasks[i].name+']に開始フラグは同じのジョブが含まれています。\n\r';
                    msg += '（' + tasks[i].jobList[k].name + 'と' + tasks[i].jobList[kk].name +'）。\n\r';
                    msg += '関連付け外すか、左メニューの「ジョブ設定」に移動して設定変更してください。';
                    setValidationAlert({ key: new Date(), message: msg, type: 'error' });
                    return;
                  }
                  break;
                }
              }
            }
          }
        }
      }
    }


    if (floorPlanStorage.tasksets){
      const newtasksets = JSON.parse(JSON.stringify(floorPlanStorage.tasksets));
      for (let i = 0; i < newtasksets.length; i++) {
        for (let k = 0; k < newtasksets[i].taskList.length; k++) {
          for (let n = 0; n < tasks.length; n++) {
            if (newtasksets[i].taskList[k].id === tasks[n].id){
              newtasksets[i].taskList[k] = tasks[n];
            }
          }
        }
      }
      setFloorPlanStorage({tasksets: newtasksets});
    }

    setSelectedStep(index);
    setFloorPlanStorage({tasks});
    setFloorPlanStorage({maxTaskID});
    setCompletedStep(completedStep > 15 ? completedStep : 15);
  }, [maxTaskID, tasks, floorPlanStorage, completedStep, setCompletedStep, setSelectedStep, setFloorPlanStorage]);

  useEffect(() => {
    if (pathUpdated) { setSelectedStep(15); }
  }, [setSelectedStep, pathUpdated]);
  /*---------route---------*/
  return (
    <Container>
      <HeaderContainer>
        <PageHeader title='タスク設定' areaHref='/' areaTitle={floorPlanStorage.completed_step === 999 ? '初期設定': '設定編集'} />
        <Buttons>
          <BackButton size='small' design='secondary' onClick={() => onContinue(14)}>{t('Common:back')}</BackButton>
          <Button design='primary' size='small' onClick={() => onContinue(16)}>{t('Common:next')}</Button>
        </Buttons>
      </HeaderContainer>

      {validationAlert.message !== '' &&
        <ValidationAlert type={validationAlert.type} message={validationAlert.message} />}

  
      <InputContainer>
        <Position>
          <InputHeader>タスク設定追加</InputHeader>
          <SmallInputContainer>
            <InputField>
              <Button design='primary' size='small' onClick={addTask}>add</Button>
            </InputField>
          </SmallInputContainer>
        </Position>
      </InputContainer>

      <InputContainer>
        <Position>
          <InputHeader>タスク選択</InputHeader>
          <SmallInputContainer>
            <SelectFieldBox value={tasks[selectedTask].name} onChange={getSelectedTask}>
              {tasks.map((item)=>{
                return <option key={'taskname_'+item.name} value={item.name}>{item.name}</option>;
              })}
            </SelectFieldBox>
          </SmallInputContainer>
        </Position>
        
        <Position>
          <InputHeader>タスク名</InputHeader>
          <SmallInputContainer>
            <InputField>
              <SmallInputBox label='' value={tasks[selectedTask].name} onChange={renameTask} />
            </InputField>
          </SmallInputContainer>
        </Position>

        <Position>
          <InputHeader>開始フラグ</InputHeader>
          <SmallInputContainer>
            <InputField>
              <SmallInputBox label='' value={tasks[selectedTask].jobList.length>0 ? tasks[selectedTask].jobList[0].name : ''} />
            </InputField>
          </SmallInputContainer>
        </Position>

        <Position>
          <InputHeader>終了フラグ</InputHeader>
          <SmallInputContainer>
            <InputField>
              <SmallInputBox label='' value={tasks[selectedTask].jobList.length>0 ? tasks[selectedTask].jobList[(tasks[selectedTask].jobList.length-1)].name : ''} />
            </InputField>
          </SmallInputContainer>
        </Position>

        <Position>
          <InputHeader>タスク削除</InputHeader>
          <SmallInputContainer>
            <Button size='small' design='primary' onClick={delTask}>－</Button>
          </SmallInputContainer>
        </Position>
      </InputContainer>

      <DragDropContext onDragEnd={onDragEnd}>
        <InputContainer>
          <Position>
            <InputHeader>{tasks[selectedTask].name}に関連付け済みジョブ</InputHeader>
          </Position>
        </InputContainer>
        <Droppable droppableId='taskJob' direction='horizontal'>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {tasks[selectedTask].jobList.map((item, index) => {
                return (
                  <Draggable key={`taskJob-${index}`} draggableId={`taskJob-${index}`} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        {item.name}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <hr />
        <JobsListTitle>
          <Position>
            <InputHeader>関連付け待ちジョブ</InputHeader>
          </Position>
        </JobsListTitle>
        <Droppable droppableId='jobsPool' direction='horizontal'>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {jobspool.map((job, index) => {
                return (
                  <Draggable key={`jobsPool-${index}`} draggableId={`jobsPool-${index}`} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        {job.name}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

    </Container>
  );
};

export default PlaceCameras;
